import {
    PaymentMethodType,
    Settings,
    PaymentMethods,
    DEFAULT_PAYMENT_METHODS
} from '@dutchinvoice/types';

/**
 * List of all payment method types (excluding 'none')
 * Derived from DEFAULT_PAYMENT_METHODS to maintain a single source of truth
 */
export const PAYMENT_METHOD_TYPES = Object.keys(DEFAULT_PAYMENT_METHODS).filter(
    (key): key is Exclude<PaymentMethodType, 'none'> => key !== 'none'
) as Exclude<PaymentMethodType, 'none'>[];

/**
 * Get enabled payment methods from payment methods object
 * @param paymentMethods Payment methods object
 * @returns Array of enabled payment method types
 */
export function getEnabledPaymentMethods(paymentMethods?: PaymentMethods): PaymentMethodType[] {
    if (!paymentMethods) return [];

    // Use Object.entries to dynamically check all payment methods
    return Object.entries(paymentMethods)
        .filter(([, data]) => data?.enabled)
        .map(([key]) => key as PaymentMethodType)
        .filter((key): key is Exclude<PaymentMethodType, 'none'> => key !== 'none');
}

/**
 * Initialize payment methods from current invoice or settings
 * @param currentPaymentMethods Current payment methods from invoice
 * @param settings User settings
 * @returns Initialized payment methods
 */
export function initializePaymentMethods(
    currentPaymentMethods?: PaymentMethods,
    settings?: Settings
): PaymentMethods {
    // Start with an empty object
    const result: PaymentMethods = {};

    // Get all possible payment method keys from the DEFAULT_PAYMENT_METHODS
    const methodKeys = Object.keys(DEFAULT_PAYMENT_METHODS) as Array<keyof PaymentMethods>;

    // Process each payment method type
    for (const key of methodKeys) {
        // First try to use current payment methods from invoice
        if (currentPaymentMethods?.[key]) {
            if (key === 'bank' && currentPaymentMethods.bank) {
                result.bank = { ...currentPaymentMethods.bank };
            } else if (key === 'paypal' && currentPaymentMethods.paypal) {
                result.paypal = { ...currentPaymentMethods.paypal };
            } else if (key === 'mollie' && currentPaymentMethods.mollie) {
                result.mollie = { ...currentPaymentMethods.mollie };
            } else if (key === 'stripe' && currentPaymentMethods.stripe) {
                result.stripe = { ...currentPaymentMethods.stripe };
            }
            continue;
        }

        // Then try to use settings
        if (settings?.paymentMethods?.[key]) {
            if (key === 'bank' && settings.paymentMethods.bank) {
                result.bank = { ...settings.paymentMethods.bank, enabled: false };
            } else if (key === 'paypal' && settings.paymentMethods.paypal) {
                result.paypal = { ...settings.paymentMethods.paypal, enabled: false };
            } else if (key === 'mollie' && settings.paymentMethods.mollie) {
                result.mollie = { ...settings.paymentMethods.mollie, enabled: false };
            } else if (key === 'stripe' && settings.paymentMethods.stripe) {
                result.stripe = { ...settings.paymentMethods.stripe, enabled: false };
            }
            continue;
        }

        // Finally use defaults
        if (DEFAULT_PAYMENT_METHODS[key]) {
            if (key === 'bank' && DEFAULT_PAYMENT_METHODS.bank) {
                result.bank = { ...DEFAULT_PAYMENT_METHODS.bank };
            } else if (key === 'paypal' && DEFAULT_PAYMENT_METHODS.paypal) {
                result.paypal = { ...DEFAULT_PAYMENT_METHODS.paypal };
            } else if (key === 'mollie' && DEFAULT_PAYMENT_METHODS.mollie) {
                result.mollie = { ...DEFAULT_PAYMENT_METHODS.mollie };
            } else if (key === 'stripe' && DEFAULT_PAYMENT_METHODS.stripe) {
                result.stripe = { ...DEFAULT_PAYMENT_METHODS.stripe };
            }
        }
    }

    return result;
}

/**
 * Create payment methods from settings for a new invoice
 * @param settings User settings
 * @returns Payment methods for the new invoice
 */
export function createPaymentMethodsFromSettings(settings: Settings): PaymentMethods | undefined {
    if (!settings.paymentMethods || !settings.defaultPaymentMethods?.length) {
        return undefined;
    }

    const result: PaymentMethods = {};
    const methodKeys = Object.keys(settings.paymentMethods) as Array<keyof PaymentMethods>;

    // Process each payment method type
    for (const key of methodKeys) {
        const methodData = settings.paymentMethods[key];
        if (methodData) {
            // Copy the payment method data from settings
            const isEnabled = settings.defaultPaymentMethods.includes(key as PaymentMethodType);

            if (key === 'bank' && settings.paymentMethods.bank) {
                result.bank = { ...settings.paymentMethods.bank, enabled: isEnabled };
            } else if (key === 'paypal' && settings.paymentMethods.paypal) {
                result.paypal = { ...settings.paymentMethods.paypal, enabled: isEnabled };
            } else if (key === 'mollie' && settings.paymentMethods.mollie) {
                result.mollie = { ...settings.paymentMethods.mollie, enabled: isEnabled };
            } else if (key === 'stripe' && settings.paymentMethods.stripe) {
                result.stripe = { ...settings.paymentMethods.stripe, enabled: isEnabled };
            }
        }
    }

    return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Update settings with payment methods
 * @param settings Current settings
 * @param paymentMethods Payment methods to save
 * @param defaultPaymentMethods Payment method types to set as default
 * @returns Updated settings
 */
export function updateSettingsWithPaymentMethods(
    settings: Settings,
    paymentMethods: PaymentMethods,
    defaultPaymentMethods: PaymentMethodType[]
): Settings {
    return {
        ...settings,
        paymentMethods: {
            ...settings.paymentMethods,
            ...paymentMethods
        },
        defaultPaymentMethods
    };
}

/**
 * Validate payment methods
 * @param paymentMethods Payment methods to validate
 * @returns True if valid, false otherwise
 */
export function validatePaymentMethods(paymentMethods?: PaymentMethods): boolean {
    if (!paymentMethods) return true;

    // Check if any payment method is enabled
    const enabledMethods = getEnabledPaymentMethods(paymentMethods);
    if (enabledMethods.length === 0) return true;

    // Validate each enabled payment method
    for (const type of enabledMethods) {
        // Validate required fields based on the method type
        if (type === 'bank') {
            const bank = paymentMethods.bank;
            if (!bank || !bank.iban || !bank.accountName) return false;
        } else if (type === 'paypal') {
            const paypal = paymentMethods.paypal;
            if (!paypal || !paypal.email) return false;
        } else if (type === 'mollie') {
            const mollie = paymentMethods.mollie;
            if (!mollie || !mollie.apiKey) return false;
        } else if (type === 'stripe') {
            const stripe = paymentMethods.stripe;
            if (!stripe || !stripe.accountId) return false;
        }
    }

    return true;
}

/**
 * Clean payment methods by removing disabled methods
 * @param paymentMethods Payment methods to clean
 * @returns Cleaned payment methods or undefined if no enabled methods
 */
export function cleanPaymentMethods(paymentMethods?: PaymentMethods): PaymentMethods | undefined {
    if (!paymentMethods) return undefined;

    const enabledMethods = getEnabledPaymentMethods(paymentMethods);
    if (enabledMethods.length === 0) return undefined;

    const result: PaymentMethods = {};

    // Only include enabled payment methods
    for (const type of enabledMethods) {
        if (type === 'bank' && paymentMethods.bank) {
            result.bank = paymentMethods.bank;
        } else if (type === 'paypal' && paymentMethods.paypal) {
            result.paypal = paymentMethods.paypal;
        } else if (type === 'mollie' && paymentMethods.mollie) {
            result.mollie = paymentMethods.mollie;
        } else if (type === 'stripe' && paymentMethods.stripe) {
            result.stripe = paymentMethods.stripe;
        }
    }

    return result;
} 